import { API_URL } from "~/lib/apiUrl";
import type { GazeChannel, GazePorts } from "~/parent/gazeIframe";

export type GazeInitData = {
  productSettings: Record<string, string>;
  /** The text of the `/ps` answer. Gaze parses it. */
  programs: string;
};

/**
 * Sends `set_auth_token` and then `init` to the iframe. The parent calls it for each
 * `on_html_loaded`.
 *
 * The iframe answers `set_auth_token` with the start of MembraneFs, and the VS Code extension
 * cannot complete `activate()` before that file system answers. So the token goes before the
 * init data, which waits for the product settings and the program list.
 *
 * `ports` is `"bridge"` in dev: the parent keeps the ports and forwards each message, so that the
 * channels stay open when the iframe page loads again. Otherwise the parent transfers the ports.
 */
export async function handshake(
  channel: GazeChannel,
  token: Promise<string>,
  data: Promise<GazeInitData>,
  user: { sub?: string },
  ports: GazePorts | "bridge",
): Promise<void> {
  channel.postMessage({ type: "set_auth_token", token: await token });

  const { productSettings, programs } = await data;
  const message = {
    type: "init",
    apiBaseUrl: API_URL,
    analyticsConfig: { auth_id: user.sub },
    programs,
    settings: productSettings,
  };
  if (ports === "bridge") {
    channel.postMessage({ ...message, useBridge: true });
  } else {
    channel.postMessage(
      { ...message, extensionPort: ports.extension, vscodePort: ports.vscode },
      [ports.extension, ports.vscode],
    );
  }
}
